import { PlusIcon } from "@heroicons/react/20/solid";
import {
  Button,
  SelectMenuDropdown,
  SelectMenuStandard,
  Table,
  useSnackBar,
} from "@truevault/aquilo";
import React, { useEffect, useState } from "react";
import { InlineLoading } from "../../../common/components/Loading";
import { useActionRequest } from "../../../common/hooks/api";
import { OrganizationUser } from "../../../common/models";
import { Api } from "../../../common/service/Api";
import { ChildOrganization } from "../../../common/service/server/controller/OrganizationVendorClientController";
import { UserRole } from "../../../common/service/server/Types";
import { Error as RequestsError } from "../../copy/requests";
import { Fmt } from "../../formatters";
import { useOrganizationUsers, usePrimaryOrganizationId, useSelf } from "../../hooks";
import { usePrimaryOrganization, useSelfOrganizationUser } from "../../hooks/useOrganization";
import { formatUserRole } from "../../util/formatters";
import { ConfirmDialog, InviteUserDialog } from "../dialog";

const useChildOrgs = (orgId: string) => {
  const [childOrgs, setChildOrgs] = useState<ChildOrganization[]>([]);
  const [childOrgUsers, setChildOrgUsers] = useState<Record<string, OrganizationUser[]>>({});

  useEffect(() => {
    const loadChildOrgs = async () => {
      try {
        const vendorClients = await Api.organizationVendorClient.getChildOrganizations(orgId);
        // Sort the organizations alphabetically by name
        const sortedOrgs = vendorClients.sort((a, b) => {
          const nameA = (a.name || "").toLowerCase();
          const nameB = (b.name || "").toLowerCase();
          return nameA.localeCompare(nameB);
        });
        setChildOrgs(sortedOrgs);

        const usersMap: Record<string, OrganizationUser[]> = {};
        for (const childOrg of sortedOrgs) {
          const response = await Api.organizationUser.getUsers(childOrg.publicId);
          usersMap[childOrg.publicId] = response;
        }
        setChildOrgUsers(usersMap);
      } catch (error) {
        console.error("Failed to load child organizations:", error);
      }
    };

    loadChildOrgs();
  }, [orgId]); // Only re-run if orgId changes

  return { childOrgs, childOrgUsers, setChildOrgUsers };
};

type UserRow = OrganizationUser & { isParentOrgUser?: boolean };

const UsersTable = ({ users, parentOrgUsers, onNeedsRefresh, allowAddingHrUser, org }) => {
  const [self] = useSelf();
  const selfOrgUser = useSelfOrganizationUser();
  const { childOrgs, childOrgUsers, setChildOrgUsers } = useChildOrgs(org.id);
  const enqueueSnackbar = useSnackBar();

  const [removeUserConfirmOpen, setRemoveUserConfirmOpen] = useState(false);
  const [selectedOrgForRemoval, setSelectedOrgForRemoval] = useState<ChildOrganization | null>(
    null,
  );
  const [userToRemove, setUserToRemove] = useState<OrganizationUser | null>(null);

  const handleRemoveFromOrg = async (userToRemove: OrganizationUser) => {
    if (!selectedOrgForRemoval) return;

    try {
      await Api.organizationUser.deleteUser(selectedOrgForRemoval.publicId, userToRemove.email);

      // Update local state after successful removal
      setChildOrgUsers({
        ...childOrgUsers,
        [selectedOrgForRemoval.publicId]:
          childOrgUsers[selectedOrgForRemoval.publicId]?.filter(
            (u) => u.email !== userToRemove.email,
          ) || [],
      });

      enqueueSnackbar(`Removed ${userToRemove.email} from ${selectedOrgForRemoval.name}`, {
        variant: "success",
      });
      onNeedsRefresh();
    } catch (error) {
      console.error("Failed to remove user from organization:", error);
      enqueueSnackbar(`Failed to remove user: ${error.message}`, {
        variant: "error",
      });
    } finally {
      setRemoveUserConfirmOpen(false);
      setSelectedOrgForRemoval(null);
      setUserToRemove(null);
    }
  };

  const roleItems = [
    { value: "ADMIN", label: "Admin" },
    { value: "GENERAL", label: "General" },
    ...(allowAddingHrUser ? [{ value: "HR", label: "HR" }] : []),
  ];

  const rows: UserRow[] = [
    ...users,
    ...parentOrgUsers.map((u) => ({ ...u, isParentOrgUser: true })),
  ];

  return (
    <>
      <Table<UserRow>
        data={rows}
        keyExtractor={(row) => `${row.email}${row.isParentOrgUser ? "-parent" : ""}`}
        columns={[
          {
            key: "user",
            header: "Authorized User",
            render: (row) => (
              <div className={row.isParentOrgUser ? "italic text-neutral-400" : undefined}>
                <div className={row.isParentOrgUser ? undefined : "text-gray-900 font-medium"}>
                  {`${row.firstName} ${row.lastName}`}
                </div>
                <div>{row.email}</div>
              </div>
            ),
          },
          {
            key: "role",
            header: "Access",
            render: (row) => {
              if (row.isParentOrgUser) {
                return (
                  <span className="italic text-neutral-400">Through {row.organizationName}</span>
                );
              }
              if (
                self?.email === row.email ||
                row.status !== "ACTIVE" ||
                selfOrgUser?.role !== "ADMIN"
              ) {
                return formatUserRole(row.role);
              }
              return (
                <div style={{ width: 120 }}>
                  <SelectMenuStandard
                    height="32px"
                    items={roleItems}
                    selectedValue={row.role}
                    onSelect={async (value) => {
                      try {
                        await Api.organizationUser.assignUserRole(
                          row.organization,
                          row.email,
                          value as UserRole,
                        );
                      } catch (error) {
                        enqueueSnackbar(`Failed to update role: ${error.message}`, {
                          variant: "error",
                        });
                      } finally {
                        // Refetch either way so the select snaps back to the saved role on failure
                        onNeedsRefresh();
                      }
                    }}
                  />
                </div>
              );
            },
          },
          {
            key: "lastLogin",
            header: "Last Login",
            render: (row) =>
              row.isParentOrgUser || row.status !== "INVITED" ? (
                Fmt.Date.fmtDate(row.lastLogin)
              ) : (
                <i>Pending</i>
              ),
          },
          {
            key: "actions",
            header: "Actions",
            render: (row) =>
              !row.isParentOrgUser && selfOrgUser?.role === "ADMIN" ? (
                <UserActions user={row} onNeedsRefresh={onNeedsRefresh} />
              ) : null,
          },
          {
            key: "orgAccess",
            header: "Organization Access",
            render: (row) => {
              if (row.isParentOrgUser) return null;
              return childOrgs.length > 0 ? (
                <SelectMenuDropdown<string>
                  items={childOrgs.map((org) => ({
                    value: org.publicId,
                    label: org.name || "Unnamed Organization",
                    isSelected:
                      childOrgUsers[org.publicId]?.some(
                        (u) => u.email === row.email && u.status !== "INACTIVE",
                      ) || false,
                  }))}
                  isLocked={selfOrgUser?.role !== "ADMIN" || row?.role === "ADMIN"}
                  onSelect={async (orgId) => {
                    const selectedOrg = childOrgs.find((org) => org.publicId === orgId);
                    if (!selectedOrg) return;

                    // Check if user is already in this org
                    const isUserInOrg = childOrgUsers[orgId]?.some(
                      (u) => u.email === row.email && u.status !== "INACTIVE",
                    );

                    if (isUserInOrg) {
                      setUserToRemove(row);
                      setSelectedOrgForRemoval(selectedOrg);
                      setRemoveUserConfirmOpen(true);
                    } else {
                      // Invite user to org
                      try {
                        await Api.organizationUser.inviteUser(selectedOrg.publicId, {
                          email: row.email,
                          firstName: row.firstName,
                          lastName: row.lastName,
                          role: row.role,
                        });

                        // Update local state after successful invite
                        const updatedUsers = [...(childOrgUsers[selectedOrg.publicId] || [])];
                        updatedUsers.push({
                          email: row.email,
                          firstName: row.firstName,
                          lastName: row.lastName,
                          role: row.role,
                          status: "ACTIVE",
                          organization: selectedOrg.publicId,
                          organizationName: selectedOrg.name,
                        });
                        setChildOrgUsers({
                          ...childOrgUsers,
                          [selectedOrg.publicId]: updatedUsers,
                        });

                        enqueueSnackbar(`Invited ${row.email} to ${selectedOrg.name}`, {
                          variant: "success",
                        });
                        onNeedsRefresh();
                      } catch (error) {
                        console.error("Failed to invite user to organization:", error);
                        enqueueSnackbar(`Failed to invite user to organization: ${error.message}`, {
                          variant: "error",
                        });
                      }
                    }
                  }}
                  allowMultipleSelected={true}
                  selectedOverflowItemName="child orgs"
                  dropdownWidthPx={350}
                />
              ) : (
                <span className="text-neutral-500">No child organizations</span>
              );
            },
          },
        ]}
      />
      <ConfirmDialog
        open={removeUserConfirmOpen}
        onClose={() => {
          setRemoveUserConfirmOpen(false);
          setSelectedOrgForRemoval(null);
          setUserToRemove(null);
        }}
        onConfirm={() => userToRemove && handleRemoveFromOrg(userToRemove)}
        confirmTitle={`Remove ${userToRemove?.email} from ${selectedOrgForRemoval?.name}?`}
        contentText={`Are you sure you want to remove ${userToRemove?.email} from ${selectedOrgForRemoval?.name}? They will no longer have access to this organization.`}
        confirmText="Remove Access"
      />
    </>
  );
};

const getUserName = (user: OrganizationUser): string => {
  if (user.firstName?.length > 0) {
    if (user.lastName?.length > 0) {
      return `${user.firstName} ${user.lastName}`;
    } else {
      return user.firstName;
    }
  } else {
    return user.email;
  }
};

const UserActions = ({
  user,
  onNeedsRefresh,
}: {
  user: OrganizationUser;
  onNeedsRefresh: () => void;
}) => {
  const [self] = useSelf();
  const { fetch: deleteUser, request: deleteRequest } = useActionRequest({
    api: () => Api.organizationUser.deleteUser(user.organization, user.email),
    messages: { defaultError: RequestsError.DeleteUser },
    onSuccess: onNeedsRefresh,
  });
  const { fetch: resendInvite } = useActionRequest({
    api: () =>
      Api.organizationUser.inviteUser(user.organization, {
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        role: user.role,
      }),
    messages: {
      success: "Invite resent successfully",
      defaultError: "Error re-sending invite",
    },
    notifySuccess: true,
    notifyError: true,
    onSuccess: onNeedsRefresh, // refresh the user list on success
  });

  const [removeUserConfirmOpen, setRemoveUserConfirmOpen] = useState(false);
  const [resendDisabled, setResendDisabled] = useState(false);

  if (deleteRequest.running) {
    return <InlineLoading />;
  }

  let userName = getUserName(user);

  const handleResendClick = () => {
    resendInvite();
    setResendDisabled(true);
    setTimeout(() => setResendDisabled(false), 5000);
  };

  if (self.email !== user.email) {
    // If the user is invited (but has not yet accepted), show "Resend Invite" + "Cancel Invite"
    if (user.status === "INVITED") {
      return (
        <>
          <div className="flex items-center gap-[8px]">
            <Button
              color="link"
              size="s"
              disabled={resendDisabled}
              onClick={() => handleResendClick()}
            >
              Resend Invite
            </Button>
            <Button
              color="warning-secondary"
              size="s"
              onClick={() => setRemoveUserConfirmOpen(true)}
            >
              Cancel Invite
            </Button>
          </div>
          <ConfirmDialog
            open={removeUserConfirmOpen}
            onClose={() => setRemoveUserConfirmOpen(false)}
            onConfirm={deleteUser}
            confirmTitle={`Cancel ${userName}'s invite?`}
            contentText={`Are you sure you want to cancel ${userName}'s invite? Once you do, they won't have access to Polaris anymore.`}
            confirmText={`Cancel Invite`}
            confirmLoading={deleteRequest.running}
          />
        </>
      );
    }

    // Otherwise, for active users, show "Remove user"
    return (
      <>
        <Button color="warning-secondary" size="s" onClick={() => setRemoveUserConfirmOpen(true)}>
          Remove user
        </Button>
        <ConfirmDialog
          open={removeUserConfirmOpen}
          onClose={() => setRemoveUserConfirmOpen(false)}
          onConfirm={deleteUser}
          confirmTitle={`Remove ${userName}?`}
          contentText={`Are you sure you want to remove ${userName}? Once you do, they won't have access to Polaris anymore.`}
          confirmText={`Remove User`}
          confirmLoading={deleteRequest.running}
        />
      </>
    );
  }

  return null;
};

export const OrganizationUsersTable = ({
  loadingElem,
  errorElemFn,
  EmptyElem,
  allowAddingHrUser = true,
}: {
  loadingElem: React.ReactElement;
  errorElemFn: (errorMessage: string) => React.ReactElement;
  EmptyElem?: React.ReactElement;
  allowAddingHrUser?: boolean;
}) => {
  const primaryOrgId = usePrimaryOrganizationId();
  const [org] = usePrimaryOrganization();
  const [users, usersRequest, refreshUsers] = useOrganizationUsers(primaryOrgId, true);
  const currentOrgUser = useSelfOrganizationUser();
  const [self] = useSelf();

  const [inviteDialog, setInviteDialog] = useState(false);

  if (usersRequest.running) {
    return loadingElem;
  } else if (!users || usersRequest.error) {
    return errorElemFn(usersRequest.errorMessage || RequestsError.GetOrgUsers);
  }

  const activeUsers = users.filter((u) => u.status !== "INACTIVE");
  const currentOrgUsers = activeUsers.filter((u) => u.organizationName === org?.name);
  const parentOrgUsers = activeUsers.filter(
    (u) => u.organizationName !== org?.name && u.organization !== "TRUEVAULT",
  );
  const sortedActiveUsers = currentOrgUsers.sort((a, b) => {
    if (a.status === "INVITED" && b.status !== "INVITED") {
      return -1;
    } else if (a.status !== "INVITED" && b.status === "INVITED") {
      return 1;
    } else {
      const aName = `${a.firstName} ${a.lastName} ${a.email}`.trim();
      const bName = `${b.firstName} ${b.lastName} ${b.email}`.trim();
      return aName.localeCompare(bName);
    }
  });

  const existingUserEmails = new Set([
    ...currentOrgUsers.map((u) => u.email.toLowerCase()),
    ...parentOrgUsers.map((u) => u.email.toLowerCase()),
  ]);

  return (
    <div className="tw-boundary user-settings">
      {(currentOrgUser?.role === "ADMIN" || self?.globalRole === "ADMIN") && (
        <>
          <Button color="primary" onClick={() => setInviteDialog(true)}>
            <span className="inline-flex items-center gap-[8px]">
              <PlusIcon className="h-5 w-5" />
              Add User
            </span>
          </Button>
          <InviteUserDialog
            open={inviteDialog}
            organizationId={primaryOrgId}
            onClose={() => setInviteDialog(false)}
            onDone={() => {
              setInviteDialog(false);
              refreshUsers();
            }}
            allowAddingHrUser={allowAddingHrUser}
            existingUserEmails={existingUserEmails}
          />
        </>
      )}
      {users?.length ? (
        <UsersTable
          users={sortedActiveUsers}
          parentOrgUsers={parentOrgUsers}
          onNeedsRefresh={refreshUsers}
          allowAddingHrUser={allowAddingHrUser}
          org={org}
        />
      ) : (
        EmptyElem
      )}
    </div>
  );
};
